import { createAction, props } from '@ngrx/store';
import { TemplateDraft as Entity, TemplateDraftStatus, FinalizeTemplateDraftResult, FinalizeTemplateDraftRequest } from './template-draft.model';
import { Template } from '../template/template.model';

// Load single draft
export const loadSingle = createAction(
    '[TemplateDraft] Load Single',
    props<{ draftId: number; correlationId?: string }>()
);

export const loadSingleSuccess = createAction(
    '[TemplateDraft] Load Single Success',
    props<{ entity: Entity; correlationId?: string }>()
);

export const loadSingleFailure = createAction(
    '[TemplateDraft] Load Single Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Load drafts by category
export const loadByCategory = createAction(
    '[TemplateDraft] Load By Category',
    props<{ categoryId: number; status?: TemplateDraftStatus; correlationId?: string }>()
);

export const loadByCategorySuccess = createAction(
    '[TemplateDraft] Load By Category Success',
    props<{ entities: Entity[]; correlationId?: string }>()
);

export const loadByCategoryFailure = createAction(
    '[TemplateDraft] Load By Category Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Create draft
export const create = createAction(
    '[TemplateDraft] Create',
    props<{ entity: Partial<Entity>; correlationId?: string }>()
);

export const createSuccess = createAction(
    '[TemplateDraft] Create Success',
    props<{ entity: Entity; correlationId?: string }>()
);

export const createFailure = createAction(
    '[TemplateDraft] Create Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Update draft
export const update = createAction(
    '[TemplateDraft] Update',
    props<{ entity: Entity; correlationId?: string }>()
);

export const updateSuccess = createAction(
    '[TemplateDraft] Update Success',
    props<{ entity: Entity; correlationId?: string }>()
);

export const updateFailure = createAction(
    '[TemplateDraft] Update Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Delete draft
export const remove = createAction(
    '[TemplateDraft] Remove',
    props<{ draftId: number; correlationId?: string }>()
);

export const removeSuccess = createAction(
    '[TemplateDraft] Remove Success',
    props<{ draftId: number; correlationId?: string }>()
);

export const removeFailure = createAction(
    '[TemplateDraft] Remove Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Add templates to draft
export const addTemplates = createAction(
    '[TemplateDraft] Add Templates',
    props<{ draftId: number; productIds: number[]; correlationId?: string }>()
);

export const addTemplatesSuccess = createAction(
    '[TemplateDraft] Add Templates Success',
    props<{ templates: Template[]; correlationId?: string }>()
);

export const addTemplatesFailure = createAction(
    '[TemplateDraft] Add Templates Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Finalize draft
export const finalize = createAction(
    '[TemplateDraft] Finalize',
    props<{ draftId: number; request?: FinalizeTemplateDraftRequest; correlationId?: string }>()
);

export const finalizeSuccess = createAction(
    '[TemplateDraft] Finalize Success',
    props<{ result: FinalizeTemplateDraftResult; draftId: number; correlationId?: string }>()
);

export const finalizeFailure = createAction(
    '[TemplateDraft] Finalize Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Start edit (reopen completed draft)
export const startEdit = createAction(
    '[TemplateDraft] Start Edit',
    props<{ draftId: number; correlationId?: string }>()
);

export const startEditSuccess = createAction(
    '[TemplateDraft] Start Edit Success',
    props<{ entity: Entity; correlationId?: string }>()
);

export const startEditFailure = createAction(
    '[TemplateDraft] Start Edit Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Edit template (create draft from existing template)
export const editTemplate = createAction(
    '[TemplateDraft] Edit Template',
    props<{ templateId: number; correlationId?: string }>()
);

export const editTemplateSuccess = createAction(
    '[TemplateDraft] Edit Template Success',
    props<{ entity: Entity; correlationId?: string }>()
);

export const editTemplateFailure = createAction(
    '[TemplateDraft] Edit Template Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Edit multiple templates (create draft from existing templates)
export const editTemplates = createAction(
    '[TemplateDraft] Edit Templates',
    props<{ templateIds: number[]; correlationId?: string }>()
);

export const editTemplatesSuccess = createAction(
    '[TemplateDraft] Edit Templates Success',
    props<{ entity: Entity; correlationId?: string }>()
);

export const editTemplatesFailure = createAction(
    '[TemplateDraft] Edit Templates Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Create order from existing templates
export const createOrder = createAction(
    '[TemplateDraft] Create Order',
    props<{ templateIds: number[]; bundleIds: number[]; categoryIds: number[]; correlationId?: string }>()
);

export const createOrderSuccess = createAction(
    '[TemplateDraft] Create Order Success',
    props<{ entity: Entity; correlationId?: string }>()
);

export const createOrderFailure = createAction(
    '[TemplateDraft] Create Order Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Edit selection (create draft from templates/bundles/categories)
export const editSelection = createAction(
    '[TemplateDraft] Edit Selection',
    props<{ templateIds: number[]; bundleIds: number[]; categoryIds: number[]; correlationId?: string }>()
);

export const editSelectionSuccess = createAction(
    '[TemplateDraft] Edit Selection Success',
    props<{ entity: Entity; correlationId?: string }>()
);

export const editSelectionFailure = createAction(
    '[TemplateDraft] Edit Selection Failure',
    props<{ error: Error; correlationId?: string }>()
);

// Local state management
export const setCurrentDraftId = createAction(
    '[TemplateDraft] Set Current Draft Id',
    props<{ draftId: number | undefined }>()
);

export const setCurrentCategoryId = createAction(
    '[TemplateDraft] Set Current Category Id',
    props<{ categoryId: number | undefined }>()
);

export const clearStore = createAction('[TemplateDraft] Clear Store');

export const TemplateDraftActions = {
    loadSingle,
    loadSingleSuccess,
    loadSingleFailure,
    loadByCategory,
    loadByCategorySuccess,
    loadByCategoryFailure,
    create,
    createSuccess,
    createFailure,
    update,
    updateSuccess,
    updateFailure,
    remove,
    removeSuccess,
    removeFailure,
    addTemplates,
    addTemplatesSuccess,
    addTemplatesFailure,
    finalize,
    finalizeSuccess,
    finalizeFailure,
    startEdit,
    startEditSuccess,
    startEditFailure,
    editTemplate,
    editTemplateSuccess,
    editTemplateFailure,
    editTemplates,
    editTemplatesSuccess,
    editTemplatesFailure,
    createOrder,
    createOrderSuccess,
    createOrderFailure,
    editSelection,
    editSelectionSuccess,
    editSelectionFailure,
    setCurrentDraftId,
    setCurrentCategoryId,
    clearStore
};
